import type { OrderDetailsOrder } from '@/types/orderDetailsOrder';

import type {
  CheckoutFragment,
  MoneyFragment,
  OrderFragment,
} from '@/graphql/saleor/api';
import { VOUCHER_TYPE } from '@/lib/const';
import type { PreOrderData } from '@/lib/preOrder';

export type OrderDataFragment = Partial<OrderFragment> | OrderDetailsOrder;

export const formatCheckoutOrder = (
  fragment: Partial<CheckoutFragment> | OrderDataFragment | null,
) => {
  return {
    subtotal:
      (fragment as CheckoutFragment)?.subtotalPrice?.net ||
      (fragment as OrderDataFragment).subtotal?.net,
    subtotalGross:
      (fragment as CheckoutFragment).subtotalPrice?.gross ||
      (fragment as OrderDataFragment).subtotal?.gross,
    shippingPrice: fragment?.shippingPrice?.net,
    grossTotalPrice:
      (fragment as CheckoutFragment).totalPrice?.gross ||
      (fragment as OrderDataFragment).total?.gross,
    netTotalPrice:
      (fragment as CheckoutFragment).totalPrice?.net ||
      (fragment as OrderDataFragment).total?.net,
    discount:
      (fragment as CheckoutFragment).discount ||
      (fragment as OrderDataFragment).discounts?.[0]?.amount,
    totalAuthorized: (fragment as OrderDataFragment).totalAuthorized,
  };
};

export const calculateSubtotal = (
  subtotal: MoneyFragment,
  discount?: MoneyFragment,
  voucherType?: string,
): MoneyFragment => {
  if (
    !discount?.amount ||
    discount.amount <= 0 ||
    voucherType === VOUCHER_TYPE.PERCENTAGE
  )
    return subtotal;

  return {
    amount: subtotal.amount + discount.amount,
    currency: subtotal.currency,
  };
};

export const getReverseDiscount = (discount: MoneyFragment): MoneyFragment => {
  return {
    ...discount,
    amount: -discount.amount,
  };
};

export const getDepositPrice = (
  fragment?: Partial<CheckoutFragment> | OrderDataFragment | null,
  preOrderData?: PreOrderData | null,
  showTax?: boolean,
) => {
  return {
    amount: showTax ? preOrderData?.deposit : preOrderData?.deposit_net,
    currency: isOrder(fragment)
      ? fragment?.total?.currency
      : fragment?.totalPrice?.currency,
  } as MoneyFragment;
};

export const getCheckoutToPayLater = (
  checkout?: Partial<CheckoutFragment> | null,
  preOrderData?: PreOrderData | null,
  showTax?: boolean,
) => {
  const depositAmount = showTax
    ? preOrderData?.deposit
    : preOrderData?.deposit_net;

  const totalPrice = showTax
    ? checkout?.totalPrice?.gross
    : checkout?.totalPrice?.net;

  return {
    ...checkout?.totalBalance,
    amount: (totalPrice?.amount ?? 0) - (depositAmount ?? 0),
  } as MoneyFragment;
};

export const getOrderToPayLater = (
  order?: Partial<OrderFragment | OrderDataFragment>,
  preOrderData?: PreOrderData | null,
  showTax?: boolean,
) => {
  const depositAmount = showTax
    ? preOrderData?.deposit
    : preOrderData?.deposit_net;

  const totalPrice = showTax ? order?.total?.gross : order?.total?.net;

  // Is used only when the order is updated with the new totalAuthorized and/or totalCharged values
  const calculatedAmount =
    (order?.total?.gross?.amount ?? 0) -
    (order?.totalAuthorized?.amount ?? 0) -
    (order?.totalCharged?.amount ?? 0);

  // final payment is the whole "pre-order balance" amount regardless of how much was already paid by split payment
  const payLaterAmount = (totalPrice?.amount ?? 0) - (depositAmount ?? 0);

  const amount = preOrderData
    ? payLaterAmount
    : order?.totalCharged?.amount === 0 && order?.totalAuthorized?.amount === 0
      ? Math.abs(order?.totalBalance?.amount ?? 0)
      : calculatedAmount;

  return {
    ...order?.totalBalance,
    amount: amount,
  } as MoneyFragment;
};

export const isOrder = (
  data?: unknown,
): data is Partial<OrderFragment> | OrderDataFragment =>
  Boolean((data as Partial<OrderFragment>)?.__typename === 'Order');
